/**
 * Provisioning runner schemas — what the workflow runner UI consumes.
 *
 * A workflow runs against a network. For each setting it checks, the API
 * returns: a compliance status, the current value (live from Meraki), and
 * the desired value (from the workflow definition). The user reviews each
 * row, then clicks Push to apply the desired state to the network.
 */
import { z } from "zod";

/** Per-setting compliance state. */
const SettingStateSchema = z.enum([
  "compliant",
  "drift",
  "missing",
  "error",
  "skipped",
]);
export type SettingState = z.infer<typeof SettingStateSchema>;

/**
 * One setting the workflow checks (e.g. "VLAN 51 subnet" or "Guest SSID auth mode").
 *
 * `current` and `desired` are JSON-serializable values rendered as pretty JSON
 * in the UI. They may be primitives (strings, numbers, bools) or nested objects.
 */
const ProvisioningSettingSchema = z.object({
  id: z.string(),
  /** Dot-separated path to the setting (e.g. "vlans.51.subnet"). */
  path: z.string(),
  /** Display name (e.g. "VLAN 51 subnet"). */
  name: z.string(),
  /** Group/category to bucket the setting under in the UI. */
  category: z.string(),
  state: SettingStateSchema,
  /** JSON-serializable current value (null when missing). */
  current: z.unknown(),
  /** JSON-serializable desired value. */
  desired: z.unknown(),
  /** Optional human note from the diagnosis (e.g. "subnet drifted from 10.21.50.0/24"). */
  note: z.string().nullable().optional(),
  /** Whether this setting will be pushed in the next provision run. */
  willPush: z.boolean(),
});
export type ProvisioningSetting = z.infer<typeof ProvisioningSettingSchema>;
const ProvisioningSettingListSchema = z.array(ProvisioningSettingSchema);

/**
 * The full diagnosis result for a workflow against a network. Drives the
 * compliance score + settings table.
 */
export const ProvisioningDiagnosisSchema = z.object({
  workflowSlug: z.string(),
  workflowName: z.string(),
  networkId: z.string(),
  networkName: z.string(),
  /** Compliance score 0-100 — % of settings that are compliant. */
  score: z.number().min(0).max(100),
  /** ISO timestamp the diagnosis was computed. */
  computedAt: z.string(),
  settings: ProvisioningSettingListSchema,
});
export type ProvisioningDiagnosis = z.infer<typeof ProvisioningDiagnosisSchema>;
